import { useEffect, useRef, useState, type Dispatch } from "react";
import { jtlLoginsLaden, nutzerAendern, nutzerAnlegen, nutzerListe, type NutzerAendernDaten } from "../../api/nutzer.ts";
import type { Aktion, Nutzer } from "../../state/reduzierer.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import { meldeFehler, meldeNachladeFehler } from "../../state/fehlerStapel.ts";
import type { NutzerAnlegenDaten } from "../Users/NutzerSeite.tsx";
import type { AktiveSeite } from "./Navigation.tsx";

interface NutzerverwaltungEingaben {
  nutzer: Nutzer | null;
  ausgewaehlteNutzerId: string | null;
  seite: AktiveSeite;
  nutzerseiteErlaubt: boolean;
  rechte: ReadonlySet<string>;
  versende: Dispatch<Aktion>;
  zeigeMeldung: (text: string) => void;
}

/**
 * Daten und Schreibaktionen der Nutzerseite; Navigation und Rechteentscheidung bleiben in App.
 * Seite, Auswahl, Nutzer-ID und Rechteidentität erhalten die bisherigen Ladeauslöser, auch
 * beim Wechsel zwischen anderen Seiten. Die Handler verwenden aktuelle Werte ohne Memoisierung.
 */
export function useNutzerverwaltung({
  nutzer,
  ausgewaehlteNutzerId,
  seite,
  nutzerseiteErlaubt,
  rechte,
  versende,
  zeigeMeldung,
}: NutzerverwaltungEingaben) {
  /**
   * Die Nutzerliste der Seite `#page-users` - EIGENSTAENDIG von der Kontoliste des Marken-Menues
   * (`MarkenMenue.tsx`, `nutzerLaden`-Prop): der Bestand fuehrt zwei getrennte Ladevorgaenge gegen
   * dieselbe Route (`loadBrandUsers` fuer die Menue-Liste, `renderUsers` fuer die volle Seite) - hier
   * ebenso zwei unabhaengige `nutzerListe()`-Aufrufe statt eines gemeinsamen Caches.
   */
  const [nutzerVerzeichnis, setNutzerVerzeichnis] = useState<readonly Nutzer[]>([]);
  const [jtlLoginOptionen, setJtlLoginOptionen] = useState<readonly string[]>([]);
  const [jtlLoginStatus, setJtlLoginStatus] = useState<"laedt" | "bereit" | "fehler">("laedt");
  const [jtlLoginFehler, setJtlLoginFehler] = useState<string>();
  const nutzerverwaltungLadefolge = useRef(0);

  /**
   * Nutzer und JTL-Logins gehören zur URL-Ansicht, nicht zu einem einzelnen Menü-Klick: ein
   * Direktaufruf, Reload oder Vor-/Zurück-Navigation auf `/nutzer` startet deshalb denselben
   * Ladevorgang. Der gemeinsame Abbruch und die Ladefolge schützen auch Attrappen, die ein
   * abgebrochenes Signal nicht selbst verwerfen, vor verspäteten Antworten.
   */
  useEffect(() => {
    if (!nutzerseiteErlaubt) {
      nutzerverwaltungLadefolge.current += 1;
      setJtlLoginOptionen([]);
      setJtlLoginStatus("laedt");
      setJtlLoginFehler(undefined);
      return;
    }

    const abbruch = new AbortController();
    const ladefolge = ++nutzerverwaltungLadefolge.current;
    setJtlLoginOptionen([]);
    setJtlLoginStatus("laedt");
    setJtlLoginFehler(undefined);

    nutzerListe(abbruch.signal)
      .then((liste) => {
        if (abbruch.signal.aborted || nutzerverwaltungLadefolge.current !== ladefolge) return;
        setNutzerVerzeichnis(liste);
      })
      .catch((ursache: unknown) => {
        if (abbruch.signal.aborted || nutzerverwaltungLadefolge.current !== ladefolge) return;
        meldeFehler(ursache, "Die Nutzerliste konnte nicht geladen werden.");
      });
    jtlLoginsLaden(abbruch.signal)
      .then((liste) => {
        if (abbruch.signal.aborted || nutzerverwaltungLadefolge.current !== ladefolge) return;
        setJtlLoginOptionen(liste);
        setJtlLoginStatus("bereit");
      })
      .catch((ursache: unknown) => {
        if (abbruch.signal.aborted || nutzerverwaltungLadefolge.current !== ladefolge) return;
        const kontext = "JTL-Benutzer konnten nicht geladen werden.";
        // Der Text steht inline (NutzerSeite) UND im Stapel; ein 401 hat keinen Text (die Anmeldemaske
        // uebernimmt), die Auswahl bleibt dann trotzdem als nicht geladen gekennzeichnet.
        const meldung = fehlertext(ursache, kontext);
        setJtlLoginFehler(meldung ?? kontext);
        setJtlLoginStatus("fehler");
        if (meldung !== null) meldeFehler(meldung);
      });

    return () => abbruch.abort();
  }, [seite, ausgewaehlteNutzerId, nutzer?.id, rechte]);

  /** `createUser` (index.html:6290-6301) - Erfolgsmeldung samt Neuladen der Liste. */
  function nutzerAnlegenAufruf(daten: NutzerAnlegenDaten): void {
    nutzerAnlegen(daten).then(
      (neu) => {
        zeigeMeldung(`Nutzer ${neu.username} angelegt`);
        // R4: angelegt ist angelegt. Scheitert nur das Nachladen der Liste, ist das KEIN Fehlschlag des
        // Anlegens („konnte nicht angelegt werden“ waere falsch), sondern ein veralteter Bildschirm.
        return nutzerListe()
          .then((liste) => setNutzerVerzeichnis(liste))
          .catch((ursache: unknown) => {
            // R-Z2: kein Wiederholungsrat der Ursache - der Nutzer legte sonst einen zweiten Nutzer an.
            meldeNachladeFehler(ursache, "Der Nutzer ist angelegt, die Liste konnte aber nicht aktualisiert werden. Bitte neu laden.");
          });
      },
      (fehler: unknown) => {
        console.error("Nutzer konnte nicht angelegt werden:", fehler);
        meldeFehler(fehler, "Der Nutzer konnte nicht angelegt werden.");
      },
    );
  }

  /**
   * `updateUser(id, changes)` (index.html:6303-6315): schreibt, aktualisiert bei Selbstaenderung
   * `zustand.nutzer` (OHNE `zustand.rechte` anzufassen - der Bestand aktualisiert an dieser Stelle
   * ebenfalls nur `CURRENT_USER`, nicht `CURRENT_PERMISSIONS`; ein Rollenwechsel wirkt sich auf die
   * eigenen Rechte erst mit der naechsten Anmeldung aus. `versende({art: "bestandGeladen", ...})`
   * trifft deshalb NUR `nutzer`, nicht den ganzen `angemeldet`-Fall, der zusaetzlich `rechte`
   * ueberschriebe), meldet per Toast und laedt die Liste neu.
   *
   * Querverweis Befund 14 (Task-Brief): `PUT /api/users/:id` erhoeht `row_version`, prueft sie aber
   * NICHT - kein 409, kein Konfliktbanner hier. Verdrahtet wie vorhanden.
   *
   * Der Parameter `erfolgsMeldung` ist am 2026-08-11 ENTFALLEN. Er trug genau einen Zweck: der
   * Passwort-Fall haengte einen Warnhinweis an, weil "Nutzer aktualisiert" dort eine Zusicherung
   * war, die der Server nicht einloeste. Seit der Endpunkt das Passwort wieder annimmt, trifft der
   * Standardtext auf ALLE vier Aufrufer zu - ein Parameter, den niemand mehr setzt, laedt nur dazu
   * ein, ihn beim naechsten Mal falsch zu setzen.
   */
  function nutzerAendernUndNeuladen(id: string, daten: NutzerAendernDaten): void {
    nutzerAendern(id, daten).then(
      (geaendert) => {
        if (nutzer && geaendert.id === nutzer.id) {
          versende({ art: "bestandGeladen", bestand: { nutzer: geaendert } });
        }
        zeigeMeldung("Nutzer aktualisiert");
        // R4, siehe `nutzerAnlegenAufruf`: gespeichert, nur die Anzeige ist veraltet.
        return nutzerListe()
          .then((liste) => setNutzerVerzeichnis(liste))
          .catch((ursache: unknown) => {
            meldeNachladeFehler(ursache, "Die Änderung ist gespeichert, die Liste konnte aber nicht aktualisiert werden. Bitte neu laden.");
          });
      },
      (fehler: unknown) => {
        console.error(`Nutzer "${id}" konnte nicht geaendert werden:`, fehler);
        meldeFehler(fehler, "Der Nutzer konnte nicht geändert werden.");
      },
    );
  }

  /** `updateUser(id, {role})` (index.html:6270/6303). */
  function nutzerRolleAendern(id: string, rolle: string): void {
    nutzerAendernUndNeuladen(id, { role: rolle });
  }

  /** `updateUser(id, {active})` (index.html:6277/6303). */
  function nutzerAktivAendern(id: string, aktiv: boolean): void {
    nutzerAendernUndNeuladen(id, { active: aktiv });
  }

  /**
   * `updateUser(id, {displayName})` (index.html:6269/6303). Die Seite ruft beim Verlassen des Felds bzw. bei Enter
   * (`NutzerSeite.tsx#AnzeigenameFeld`), nicht je Tastendruck - der Koerper enthaelt nur den Namen.
   */
  function nutzerAnzeigenameAendern(id: string, anzeigename: string): void {
    nutzerAendernUndNeuladen(id, { displayName: anzeigename });
  }

  /**
   * `updateUser(id, {jtlLogin})` - Auftrag "JTL-Login in der Nutzerverwaltung" (2026-09-07), kein
   * Vorbild in `index.html` (siehe Kopfkommentar an `NutzerSeite.tsx#onJtlLoginAendern`). Dasselbe
   * Muster wie `nutzerAnzeigenameAendern` direkt darueber: `nutzerAendernUndNeuladen` meldet einen
   * Fehlschlag bereits im Fehlerstapel (siehe deren Code oben) - ein ungueltiger Login (400 von
   * `PUT /api/users/:id`, `nummerAusJtl`) erscheint deshalb ohne weiteres Zutun dort, mit dem Servertext.
   *
   * `wert === ""` ist die Auswahl "kein JTL-Login" bei einem Konto "Nur lesen" und loescht den Login; der Server
   * erlaubt das nur, wenn die Rolle danach `read_only` ist (sonst 400, im Fehlerstapel wie jeder andere Fehler).
   */
  function nutzerJtlLoginAendern(id: string, wert: string): void {
    nutzerAendernUndNeuladen(id, { jtlLogin: wert });
  }

  /**
   * `resetUserPassword(id, username)` (index.html:6317-6321) - woertlich inklusive `prompt()`.
   *
   * SEIT 2026-08-11 WIRKT DIESER AUFRUF WIRKLICH. Bis dahin las `PUT /api/users/:id` aus dem
   * Koerper ausschliesslich `displayName`/`role`/`active` - `password` fiel still auf den Boden.
   * Das war eine PORTIERUNGSREGRESSION, keine nie dagewesene Luecke: der Bestand-Worker verarbeitete
   * `body.password` im PUT-Zweig sehr wohl (Mindestlaenge acht, neues Salt, `passwordHash`). Der
   * Express-Port hatte den Zweig stillschweigend fallen gelassen, und KEIN Backend-Test deckte ihn
   * ab - die Integrationstests kannten `password` nur beim Anlegen. Genau daran ist es
   * durchgerutscht.
   *
   * Der Endpunkt nimmt das Feld jetzt wieder an, und der Test dort prueft den einzigen Nachweis,
   * der zaehlt: die Anmeldung mit dem NEUEN Passwort gelingt und mit dem alten scheitert
   * (`backend/test/integration/users-routes.test.ts`).
   *
   * MIT DER REPARATUR IST DER WARNHINWEIS ENTFALLEN, den diese Funktion bis dahin an die
   * Erfolgsmeldung haengte ("das Passwort wurde NICHT geändert"). Er war richtig, solange der
   * Endpunkt nichts tat; danach war er die gefaehrlichere Falschaussage - ein Administrator gibt
   * auf sie hin das alte Passwort weiter. Siehe den Block bei `alsAufzaehlung` in `App.tsx`, warum das
   * ausgerechnet KEIN Test bemerkte.
   *
   * Die Meldung lautet damit wieder schlicht "Nutzer aktualisiert", wie im Bestand - der Aufruf
   * selbst war ohnehin nie veraendert.
   *
   * OFFEN und beim Auftraggeber: bestehende Sitzungen ueberleben einen Passwortwechsel. Der Bestand
   * loescht Sitzungen nur beim Deaktivieren; das ist woertlich portiert. Im Sicherheitsfall bleibt
   * ein kompromittiertes Token damit bis zu seinem Ablauf gueltig.
   */
  function nutzerPasswortZuruecksetzen(id: string, username: string): void {
    const password = prompt(`Neues Passwort für ${username} (mindestens 8 Zeichen):`);
    if (password === null) return;
    if (password.length < 8) {
      meldeFehler("Passwort muss mindestens 8 Zeichen lang sein");
      return;
    }
    nutzerAendernUndNeuladen(id, { password });
  }

  return {
    nutzerVerzeichnis,
    jtlLoginOptionen,
    jtlLoginStatus,
    jtlLoginFehler,
    nutzerAnlegenAufruf,
    nutzerRolleAendern,
    nutzerAktivAendern,
    nutzerAnzeigenameAendern,
    nutzerJtlLoginAendern,
    nutzerPasswortZuruecksetzen,
  };
}
